import { useRef } from "react";
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
  useScroll,
} from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { useLang } from "@/i18n";
import { scrollToId } from "./Navbar";

const HERO_IMG =
  "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?auto=format&fit=crop&w=1200&q=80";

const Hero = () => {
  const { t, lang } = useLang();
  const sectionRef = useRef(null);
  const cardRef = useRef(null);

  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rX = useSpring(useTransform(my, [0, 1], [7, -7]), { stiffness: 120, damping: 18 });
  const rY = useSpring(useTransform(mx, [0, 1], [-9, 9]), { stiffness: 120, damping: 18 });

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const onMove = (e) => {
    const r = cardRef.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };

  return (
    <section
      id="inicio"
      ref={sectionRef}
      data-testid="hero-section"
      className="relative flex min-h-screen flex-col overflow-hidden bg-charcoal text-stone-200"
    >
      <div className="bg-blueprint absolute inset-0" aria-hidden="true" />
      <div
        className="pointer-events-none absolute -right-10 top-24 hidden select-none font-display text-[11rem] font-semibold leading-none tracking-tight text-outline lg:block"
        aria-hidden="true"
      >
        OM
      </div>

      <motion.div
        style={{ opacity: fade }}
        className="relative mx-auto grid w-full max-w-7xl flex-1 items-center gap-14 px-5 pb-16 pt-32 lg:grid-cols-12 lg:px-10 lg:pt-36"
      >
        <div className="lg:col-span-7">
          <motion.p
            key={`eyebrow-${lang}`}
            data-testid="hero-eyebrow"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="mb-8 font-mono text-[11px] uppercase tracking-[0.3em] text-terracotta"
          >
            {t.hero.eyebrow}
          </motion.p>

          <h1
            data-testid="hero-title"
            className="font-display text-4xl font-medium leading-[1.06] tracking-tight text-bone sm:text-5xl lg:text-6xl"
          >
            {t.hero.lines.map((line, i) => (
              <span key={`${lang}-${i}`} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{
                    duration: 0.95,
                    delay: 0.18 + i * 0.13,
                    ease: [0.16, 1, 0.3, 1],
                  }}
                >
                  {i === 2 ? (
                    <em className="text-terracotta">{line}</em>
                  ) : (
                    line
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            key={`sub-${lang}`}
            data-testid="hero-subtitle"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.62, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 max-w-xl text-base leading-relaxed text-stone-400"
          >
            {t.hero.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.78, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-quote"
              onClick={() => scrollToId("#presupuesto")}
              className="group flex items-center gap-3 rounded-full bg-terracotta px-7 py-4 font-mono text-xs uppercase tracking-[0.18em] text-bone transition-colors duration-300 hover:bg-terra-dark"
            >
              {t.hero.ctaPrimary}
              <ArrowUpRight
                size={15}
                className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </button>
            <button
              data-testid="hero-cta-projects"
              onClick={() => scrollToId("#proyectos")}
              className="rounded-full border border-white/20 px-7 py-4 font-mono text-xs uppercase tracking-[0.18em] text-stone-300 transition-colors duration-300 hover:border-terracotta hover:text-terracotta"
            >
              {t.hero.ctaSecondary}
            </button>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="relative lg:col-span-5"
          style={{ perspective: 1100 }}
        >
          <div
            ref={cardRef}
            onMouseMove={onMove}
            onMouseLeave={() => {
              mx.set(0.5);
              my.set(0.5);
            }}
            className="relative mx-auto max-w-md"
            data-testid="hero-tilt-card"
          >
            <div className="absolute -left-4 -top-4 h-full w-full border border-terracotta/60" aria-hidden="true" />
            <motion.div
              style={{ rotateX: rX, rotateY: rY, transformStyle: "preserve-3d" }}
              className="relative overflow-hidden bg-charcoal-2 shadow-2xl shadow-black/50"
            >
              <motion.img
                src={HERO_IMG}
                alt={t.hero.cardCaption}
                style={{ y: imgY, scale: 1.16 }}
                className="aspect-[4/5] w-full object-cover"
              />
              <div className="absolute inset-x-0 bottom-0 flex items-end justify-between bg-charcoal/70 p-5 backdrop-blur-sm">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-terracotta">
                    Obras Martinez
                  </p>
                  <p className="mt-1 font-display text-lg text-bone">{t.hero.cardCaption}</p>
                </div>
                <span className="font-mono text-[10px] tracking-widest text-stone-400">
                  {t.hero.cardChip}
                </span>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.05 }}
        className="relative border-t border-white/10"
        data-testid="hero-stats-strip"
      >
        <div className="mx-auto grid max-w-7xl grid-cols-2 divide-x divide-white/10 lg:grid-cols-4">
          {t.hero.stats.map((s, i) => (
            <div key={`${lang}-stat-${i}`} className="px-5 py-6 lg:px-8">
              <p className="font-mono text-2xl font-medium text-terracotta">{s.value}</p>
              <p className="mt-1 text-xs uppercase tracking-[0.14em] text-stone-500">
                {s.label}
              </p>
            </div>
          ))}
        </div>
        <div className="pointer-events-none absolute -top-16 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 text-stone-500 lg:flex">
          <span className="font-mono text-[10px] uppercase tracking-[0.3em]">{t.hero.scroll}</span>
          <ArrowDown size={14} className="animate-bounce" />
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
